import React, { useEffect, useState } from 'react'
import { Alert, FormControl, MenuItem, Select, Tooltip, Typography } from '@mui/material'
import { fetchAssistantModels, type AssistantModelOption, type AssistantModelsResponse } from './assistantModelsClient'

interface AssistantModelPickerProps {
  /** Saved choice; empty uses the server default. */
  value: string
  onChange: (model: string) => void
  /** Fetch only while the gear is open. */
  active: boolean
}

function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`
}

function formatPerM(value: number | null): string {
  if (value == null) return '—'
  return `$${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)} / 1M`
}

function priceTooltip(model: AssistantModelOption): string {
  return `Input ${formatPerM(model.inputPer1M)} · Output ${formatPerM(model.outputPer1M)}`
}

function relativeLabel(model: AssistantModelOption): string {
  if (model.relativeCost == null) return ''
  const n = model.relativeCost
  const text = Number.isInteger(n) ? String(n) : n.toFixed(1)
  return ` (×${text})`
}

/** Approved AI models with a relative cost and this month's remaining budget. */
export const AssistantModelPicker: React.FC<AssistantModelPickerProps> = ({ value, onChange, active }) => {
  const [data, setData] = useState<AssistantModelsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!active) return
    let cancelled = false
    setError(null)
    void fetchAssistantModels()
      .then(next => {
        if (!cancelled) setData(next)
      })
      .catch(() => {
        if (!cancelled) setError('Could not load models')
      })
    return () => {
      cancelled = true
    }
  }, [active])

  const models = data?.models ?? []
  const selected = models.some(model => model.id === value) ? value : (data?.defaultModel ?? '')
  const budget = data?.budget

  return (
    <>
      <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
        AI model
      </Typography>
      <FormControl fullWidth size='small' sx={{ mb: 0.5 }}>
        <Select
          value={selected}
          displayEmpty
          disabled={models.length === 0}
          onChange={event => onChange(String(event.target.value))}
          inputProps={{ 'aria-label': 'Assistant AI model' }}
        >
          {models.length === 0 ? <MenuItem value=''>{error ?? 'Loading…'}</MenuItem> : null}
          {models.map(model => (
            <MenuItem key={model.id} value={model.id}>
              <Tooltip title={priceTooltip(model)} placement='left' enterDelay={400}>
                <span>
                  {model.label}
                  {relativeLabel(model)}
                  {model.id === data?.defaultModel ? ' (default)' : ''}
                </span>
              </Tooltip>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {budget?.blocked ? (
        <Alert severity='error' sx={{ mb: 2, py: 0 }}>
          Monthly AI budget used up. The assistant resumes on the 1st.
        </Alert>
      ) : budget?.capReached ? (
        <Alert severity='warning' sx={{ mb: 2, py: 0 }}>
          Monthly budget of {formatUsd(budget.capUsd)} reached. Replies use the default model until the 1st.
        </Alert>
      ) : (
        <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 2 }}>
          {budget
            ? `${formatUsd(budget.remainingUsd)} of ${formatUsd(budget.capUsd)} left this month`
            : (error ?? '\u00a0')}
        </Typography>
      )}
    </>
  )
}
